import type { UiStrings } from '../ui';

/**
 * Arabic interface strings, from sections 24 and 27 of the localization pack.
 *
 * `cookies` and `legal` below are drafts and are **not** what `/ar` renders:
 * `localized.ts` substitutes the approved English text for both until their
 * flags flip in `./approval.ts`. They live here so reviewers have concrete copy
 * to sign off on.
 */
export const ui: UiStrings = {
  newsletter: {
    emailPlaceholder: 'اكتب بريدك الإلكتروني',
    emailLabel: 'البريد الإلكتروني',
    subscribe: 'اشترك',
    subscribing: 'جاري الاشتراك…',
    success: 'تمام، اتسجلت في القائمة.',
    alreadySubscribed: 'إنت مشترك بالفعل.',
    invalidEmail: 'اكتب بريد إلكتروني صحيح.',
    unavailable: 'الاشتراك في النشرة مش متاح دلوقتي. جرّب تاني بعد شوية.',
    error: 'حصلت مشكلة أثناء الإرسال. جرّب تاني.',
  },
  footer: {
    regionalVersionsLabel: 'النسخ الإقليمية',
    languageLabel: 'اللغة',
  },
  pricing: {
    plansHeading: 'اختار الباقة المناسبة ليك',
    seeFullPricing: 'شوف كل الأسعار',
    comingSoon: 'قريبًا',
    sameOnEveryPlan: 'في كل الباقات',
    heading: 'خليك محتفظ بأكبر جزء من فلوسك',
    personal: 'أفراد',
    business: 'شركات',
    seeAllTerms: 'شوف كل الشروط والأحكام',
  },
  business: {
    testimonialsTitle: 'شركات من كل مكان واثقة في nsave',
  },
  close: 'إغلاق',
  testimonials: {
    previous: 'الآراء السابقة',
    next: 'الآراء التالية',
    rating: '5 من 5 نجوم',
  },
  calculator: {
    youSend: 'أنت ترسل',
    recipientGets: 'المستلم يحصل على',
    exchangeRate: 'سعر الصرف',
    currentRate: 'السعر الحالي',
    fees: 'الرسوم',
    pricingNote: 'الأسعار تنطبق على الحسابات الشخصية فقط',
    amountPlaceholder: 'أدخل المبلغ',
    amountLabel: 'المبلغ المُرسل',
    back: 'رجوع',
    selectCurrency: 'اختر العملة',
    selectPlan: 'اختر الخطة',
    transferFee: 'رسوم التحويل',
    amount: 'المبلغ',
    amountTooLow: 'المبلغ أقل من الحد',
    plans: { PRO: 'خطة برو', FREE: 'الخطة المجانية' },
    changeCurrency: 'تغيير العملة الحالية',
    changePlan: 'تغيير الخطة الحالية',
    from: 'من',
    to: 'إلى',
    currencyNames: {
      GBP: 'الجنيه الإسترليني',
      USD: 'الدولار الأمريكي',
      EUR: 'اليورو',
      EGP: 'الجنيه المصري',
      BDT: 'التاكا البنغلاديشية',
      PKR: 'الروبية الباكستانية',
      NGN: 'النايرا النيجيرية',
      MAD: 'الدرهم المغربي',
      TRY: 'الليرة التركية',
      SYP: 'الليرة السورية',
    },
    competitorRatesLoading: 'جارٍ تحميل أسعار المنافسين المباشرة…',
    competitorRatesUnavailable: 'أسعار المنافسين المباشرة غير متاحة حاليًا. يُرجى المحاولة لاحقًا.',
    saveUpTo: 'وفّر حتى',
  },
  downloadQr: {
    label: 'نزّل nsave',
  },
  header: {
    learn: 'اتعلّم مع nsave',
    homeLabel: 'الصفحة الرئيسية لـnsave',
    mainNavLabel: 'التنقل الرئيسي',
  },
  // ⚠️ DRAFT, not from the localization pack — see the note on `UiStrings.cookies`.
  // Gated off by `AR_COPY_APPROVED.cookies`; `/ar` shows the English banner.
  cookies: {
    dialogLabel: 'إعدادات ملفات تعريف الارتباط',
    title: 'إحنا بنستخدم ملفات تعريف الارتباط',
    bodyBeforeLink:
      'بنستخدم ملفات تعريف الارتباط وتقنيات مشابهة لتحليل الزيارات وتحسين nsave. اقرا ',
    privacyLinkLabel: 'سياسة الخصوصية',
    bodyAfterLink: '.',
    accept: 'موافق',
    reject: 'رفض',
  },
  legal: {
    // Modern Standard Arabic, per the pack's rule that legal and regulatory
    // copy is not written in Egyptian Arabic.
    //
    // ⚠️ DRAFT — pack section 27 states this is "a linguistic draft only" and
    // must be replaced verbatim with the text nsave's legal department and the
    // relevant regulators approve. Do not treat it as a compliant disclosure.
    // Gated off by `AR_COPY_APPROVED.legal`; `/ar` shows the English footer.
    copyright: (year) => `حقوق الطبع والنشر © ${year} nsave Ltd. جميع الحقوق محفوظة.`,
    paragraphs: [
      'هذا الموقع مملوك ومدار بواسطة nsave Ltd.، وهي شركة مسجلة في المملكة المتحدة. المعلومات المقدمة غير ملزمة قانونًا ولا تُعد عرضًا أو اعتمادًا أو توصية أو دعوة للدخول في أي معاملة مالية، سواء في هذه الولاية القضائية أو في أي ولاية قضائية أخرى يكون فيها هذا العرض أو هذه الدعوة غير قانونيين بموجب القوانين المعمول بها.',
      'شركة npay Inc. مسجلة في ولاية ديلاوير بالولايات المتحدة. تتعاون npay Inc. مع مؤسسات مالية مرخصة، وهي مقدم خدمات تكنولوجية وليست بنكًا.',
      'شركة npay USA Inc. مسجلة في ولاية مونتانا بالولايات المتحدة ومسجلة كشركة خدمات مالية لدى شبكة مكافحة الجرائم المالية (FinCEN) تحت رقم تسجيل MSB: 31000342363468.',
      'تُقدم حسابات GBP والحسابات متعددة العملات بواسطة Keel Money Ltd، وهي مؤسسة أموال إلكترونية مرخصة من هيئة السلوك المالي البريطانية بموجب لوائح الأموال الإلكترونية لعام 2011، رقم التسجيل 1020783. تُحفظ أموال العملاء لدى مؤسسات ائتمانية مرخصة في المملكة المتحدة أو المنطقة الاقتصادية الأوروبية، لكنها غير مشمولة بحماية نظام تعويضات الخدمات المالية (FSCS).',
      'تم تعيين nsave Ltd. بموجب المادة 33 من لوائح الأموال الإلكترونية لتوزيع و/أو استرداد الأموال الإلكترونية نيابةً عن Keel Money Ltd.، ولا تملك nsave Ltd. بذاتها ترخيصًا لإصدار الأموال الإلكترونية أو تقديم خدمات الدفع. تتوفر تفاصيل إضافية في شروط وأحكام المستخدم النهائي الخاصة بـKeel، والتي يجب الموافقة عليها قبل استخدام أي خدمات تقدمها Keel Money Ltd.',
    ],
  },
};
